import { ICaseSearchModel, ICaseStatusLookupModel } from '../../interfaces/ICase';

import { AppliedFiltersSection } from '../shared/filters/AppliedFiltersSection';
import { AppliedFiltersView } from '../shared/filters/AppliedFiltersView';
import { CaseFilterType } from '../../utilities/Constants';
import { FilterBadge } from '../shared/filters/FilterBadge';
import { ISelectedFiltersDetails } from '../shared/filters/AppliedFiltersDetailsBody';
import { cloneDeep } from 'lodash';
import { useCaseManagersInZone } from '../../shared/react-query-hooks/useCaseManagersInZone';
import { useGetCaseStatuses } from '../quad/same-case-easy-update/useGetCaseStatuses';
import { useGetDiagnoses } from '../../shared/react-query-hooks/useGetDiagnoses';
import { useGetPlaintiffsFirms } from '../../shared/react-query-hooks/useGetPlaintiffsFirms';
import { useGetQScores } from '../../shared/react-query-hooks/useGetQScores';
import { useGetUsStatesLookup } from '../../shared/react-query-hooks/useUsStatesLookup';
import { useGetZoneJurisdictions } from '../../shared/react-query-hooks/useGetZoneJurisdictions';

interface ICaseListAppliedFiltersProps {
    searchModel: ICaseSearchModel;
    onApplyFilters: (filters: ICaseSearchModel) => void;
    onToggleFilterPane: () => void;
}

export function CaseListAppliedFilters(props: ICaseListAppliedFiltersProps) {
    const caseStatusTypesApi = useGetCaseStatuses();
    const jurisdictionsApi = useGetZoneJurisdictions();
    const diagnosesApi = useGetDiagnoses();
    const statesApi = useGetUsStatesLookup();
    const plaintiffsFirmsApi = useGetPlaintiffsFirms();
    const caseManagersApi = useCaseManagersInZone();
    const sqScoresApi = useGetQScores();

    const handleRemoveSearchText = () => {
        const newFilters: ICaseSearchModel = {
            ...props.searchModel,
            searchText: '',
        };

        props.onApplyFilters(newFilters);
    };

    const handleRemoveStatus = (item: ICaseStatusLookupModel) => {
        const { searchModel, onApplyFilters } = props;
        const { subStatusIds = [], statusIds = [] } = searchModel;

        const updatedSubStatusIds = subStatusIds.filter((id) => id !== item.subStatus?.id);

        const statusTypes = caseStatusTypesApi.data ?? [];
        const matchingStatusTypes = statusTypes.filter((statusType) =>
            subStatusIds.includes(statusType.subStatus?.id ?? -1)
        );

        const updatedStatusIds =
            matchingStatusTypes.length === 1
                ? statusIds.filter((id) => id !== item.status.id)
                : statusIds;

        onApplyFilters({
            ...cloneDeep(searchModel),
            subStatusIds: updatedSubStatusIds,
            statusIds: updatedStatusIds,
        });
    };

    const handleRemoveLookupFilter = (filterTypeId: number, value: string) => {
        const searchModelCopy = cloneDeep(props.searchModel);

        const filteredFilters = searchModelCopy.filters?.filter(
            (filter) => !(filter.typeId === filterTypeId && filter.text === value)
        );

        const newSearchModel: ICaseSearchModel = {
            ...props.searchModel,
            filters: filteredFilters,
        };

        props.onApplyFilters(newSearchModel);
    };

    const sortedSelectedStatuses = caseStatusTypesApi?.data?.filter(
        (caseStatus) =>
            props.searchModel.statusIds?.includes(caseStatus.status.id) &&
            (caseStatus.subStatus == null ||
                props.searchModel.subStatusIds?.includes(caseStatus.subStatus.id))
    );

    const sortedSelectedDiagnoses = props.searchModel.filters
        ?.filter((filter) => filter.typeId === CaseFilterType.Diagnosis.Value)
        .sort((a, b) => a.text.localeCompare(b.text));

    const sortedSelectedJurisdictions = props.searchModel.filters
        ?.filter((filter) => filter.typeId === CaseFilterType.Jurisdiction.Value)
        .sort((a, b) => a.text.localeCompare(b.text));

    const sortedSelectedStates = props.searchModel.filters
        ?.filter((filter) => filter.typeId === CaseFilterType.State.Value)
        ?.map((filter) => statesApi.data?.find((x) => x.name?.toString() === filter.text))
        .sort((a, b) => {
            const nameA = a?.displayName ?? a?.name ?? '';
            const nameB = b?.displayName ?? b?.name ?? '';
            return nameA.localeCompare(nameB);
        });

    const sortedSelectedFirms = props.searchModel.filters
        ?.filter((filter) => filter.typeId === CaseFilterType.PlaintiffsFirm.Value)
        .sort((a, b) => a.text.localeCompare(b.text));

    const sortedSelectedCaseManagers = props.searchModel.filters
        ?.filter((filter) => filter.typeId === CaseFilterType.CaseManager.Value)
        ?.map((filter) => caseManagersApi.data?.find((x) => x.guid?.toString() === filter.text))
        .sort((a, b) => {
            const nameA = `${a?.profile?.firstName} ${a?.profile?.lastName}`;
            const nameB = `${b?.profile?.firstName} ${b?.profile?.lastName}`;
            return nameA.localeCompare(nameB);
        });

    const sortedSelectedSqScores = props.searchModel.filters
        ?.filter((filter) => filter.typeId === CaseFilterType.QScore.Value)
        .sort((a, b) => a.text.localeCompare(b.text));

    const details: ISelectedFiltersDetails[] = [
        {
            label: 'Search Text',
            values: props.searchModel.searchText ? [props.searchModel.searchText] : [],
        },
        { label: 'Statuses', values: sortedSelectedStatuses?.map((item) => item.name) || [] },
        { label: 'Diagnoses', values: sortedSelectedDiagnoses?.map((item) => item.text) || [] },
        {
            label: 'Jurisdictions',
            values: sortedSelectedJurisdictions?.map((item) => item.text) || [],
        },
        {
            label: 'States',
            values:
                sortedSelectedStates?.map((item) => item?.displayName ?? item?.name ?? '') || [],
        },
        { label: 'Plaintiffs Firms', values: sortedSelectedFirms?.map((item) => item.text) || [] },
        {
            label: 'Case Managers',
            values:
                sortedSelectedCaseManagers?.map(
                    (item) => `${item?.profile?.firstName} ${item?.profile?.lastName}`
                ) || [],
        },
        { label: 'SQ Scores', values: sortedSelectedSqScores?.map((item) => item.text) || [] },
    ];

    // hack for CHECK-3483
    const hasOnlyOneFilterApplied =
        details.map((detail) => detail.values.length).reduce((count, curr) => (curr += count)) ===
        1;

    return (
        <AppliedFiltersSection onToggleFilterPane={props.onToggleFilterPane}>
            <AppliedFiltersView selectedFilterDetails={details}>
                <>
                    {props.searchModel.searchText && (
                        <FilterBadge
                            key={'search-text-filter'}
                            label={props.searchModel.searchText}
                            onRemove={() => handleRemoveSearchText()}
                            disabled={hasOnlyOneFilterApplied}
                        />
                    )}

                    {sortedSelectedStatuses?.map((item) => (
                        <FilterBadge
                            key={item.id}
                            label={item.name}
                            onRemove={() => handleRemoveStatus(item)}
                            isLoading={caseStatusTypesApi.isFetching}
                            disabled={hasOnlyOneFilterApplied}
                        />
                    ))}

                    {sortedSelectedDiagnoses?.map((item) => (
                        <FilterBadge
                            key={item.typeId + item.text}
                            label={item.text}
                            onRemove={() =>
                                handleRemoveLookupFilter(
                                    CaseFilterType.Diagnosis.Value,
                                    item?.text?.toString() ?? ''
                                )
                            }
                            isLoading={diagnosesApi.isFetching}
                            disabled={hasOnlyOneFilterApplied}
                        />
                    ))}

                    {sortedSelectedJurisdictions?.map((item) => (
                        <FilterBadge
                            key={item.typeId + item.text}
                            label={item.text}
                            onRemove={() =>
                                handleRemoveLookupFilter(
                                    CaseFilterType.Jurisdiction.Value,
                                    item?.text?.toString() ?? ''
                                )
                            }
                            isLoading={jurisdictionsApi.isFetching}
                            disabled={hasOnlyOneFilterApplied}
                        />
                    ))}

                    {sortedSelectedStates?.map((item) => (
                        <FilterBadge
                            key={item?.id}
                            label={item?.displayName ?? item?.name}
                            onRemove={() =>
                                handleRemoveLookupFilter(
                                    CaseFilterType.State.Value,
                                    item?.name?.toString() ?? ''
                                )
                            }
                            isLoading={statesApi.isFetching}
                            disabled={hasOnlyOneFilterApplied}
                        />
                    ))}

                    {sortedSelectedFirms?.map((item) => (
                        <FilterBadge
                            key={item.typeId + item.text}
                            label={item.text}
                            onRemove={() =>
                                handleRemoveLookupFilter(
                                    CaseFilterType.PlaintiffsFirm.Value,
                                    item?.text.toString() ?? ''
                                )
                            }
                            isLoading={plaintiffsFirmsApi.isFetching}
                            disabled={hasOnlyOneFilterApplied}
                        />
                    ))}

                    {sortedSelectedCaseManagers?.map((item) => (
                        <FilterBadge
                            key={item?.guid}
                            label={`${item?.profile?.firstName} ${item?.profile?.lastName}`}
                            onRemove={() =>
                                handleRemoveLookupFilter(
                                    CaseFilterType.CaseManager.Value,
                                    item?.guid ?? ''
                                )
                            }
                            isLoading={caseManagersApi.isFetching}
                            disabled={hasOnlyOneFilterApplied}
                        />
                    ))}

                    {sortedSelectedSqScores?.map((item) => (
                        <FilterBadge
                            key={item.typeId + item.text}
                            label={item.text}
                            onRemove={() =>
                                handleRemoveLookupFilter(
                                    CaseFilterType.QScore.Value,
                                    item?.text.toString() ?? ''
                                )
                            }
                            isLoading={sqScoresApi.isFetching}
                            disabled={hasOnlyOneFilterApplied}
                        />
                    ))}
                </>
            </AppliedFiltersView>
        </AppliedFiltersSection>
    );
}
